import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TicketsAdmin from "@/components/TicketsAdmin";
import MatchesAdmin from "@/components/MatchesAdmin";
import { ShieldCheck, Users, Clock, BadgeCheck, Swords, LogOut } from "lucide-react";

const inputCls =
  "w-full bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors duration-300 text-sm";
const labelCls = "block font-mono2 text-[10px] tracking-[0.3em] text-zinc-400 uppercase mb-2";

const STATUS_CLS = {
  pending_review: "text-yellow-500 border-yellow-800/60",
  pending_review: "text-yellow-500 border-yellow-800/60",
  approved: "text-green-500 border-green-800/60",
  rejected: "text-red-500 border-red-800/60",
};

function UserEditor({ u, apps, reload, onDone }) {
  const app = apps.find((a) => a.user_id === u.id);
  const [form, setForm] = useState({ name: u.name, email: u.email, password: "", phone: app?.phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: form.name, email: form.email, phone: form.phone };
      if (form.password) payload.password = form.password;
      await api.put(`/admin/users/${u.id}`, payload);
      toast.success(`Updated ${form.email}`);
      onDone();
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 pt-4 border-t border-zinc-800/80 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid={`user-editor-${u.id}`}>
      <input className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`edit-name-${u.id}`} />
      <input className={inputCls} type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`edit-email-${u.id}`} />
      <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={`edit-phone-${u.id}`} />
      <input className={inputCls} type="text" placeholder="New password (optional)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid={`edit-password-${u.id}`} />
      <button onClick={save} disabled={busy} data-testid={`edit-save-${u.id}`} className="font-cond font-semibold tracking-[0.2em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-6 py-3 transition-colors">
        {busy ? "SAVING..." : "SAVE CHANGES"}
      </button>
      <p className="sm:col-span-2 lg:col-span-5 font-mono2 text-[9px] tracking-[0.2em] text-zinc-600 uppercase">
        Existing passwords are one-way encrypted and can never be displayed — set a new one above and it takes effect immediately.
      </p>
    </div>
  );
}

function RecordEditor({ app, reload }) {
  const [val, setVal] = useState(app.pro_record || "0-0-0");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/registrations/${app.id}/record`, { pro_record: val.trim() });
      toast.success(`Record updated for ${app.full_name}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2 mt-3">
      <span className="font-mono2 text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Record W-L-D</span>
      <input
        className="w-28 bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-2 py-1.5 text-sm text-white transition-colors"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid={`record-input-${app.id}`}
      />
      <button
        onClick={save}
        disabled={busy}
        data-testid={`record-save-${app.id}`}
        className="font-cond font-semibold tracking-[0.2em] text-xs border border-zinc-700 hover:border-[#E10600] hover:text-[#E10600] disabled:opacity-40 text-zinc-300 px-4 py-1.5 transition-colors duration-300"
      >
        {busy ? "..." : "SAVE"}
      </button>
    </div>
  );
}

function AdminLogin({ onDone }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.user.role !== "admin") {
        toast.error("This account is not an admin");
      } else {
        onDone(data.user);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="max-w-md mx-auto px-4 pt-36 pb-24" data-testid="admin-login-panel">
      <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#E10600] uppercase mb-4 flex items-center gap-4">
        <span className="h-px w-10 bg-[#E10600]" /> Restricted Corner
      </p>
      <h1 className="font-display text-5xl uppercase text-white mb-8">Admin Access</h1>
      <form onSubmit={submit} className="border border-zinc-800 bg-[#111111] p-6 sm:p-8 space-y-6" data-testid="admin-login-form">
        <div>
          <label className={labelCls} htmlFor="admin-email">Admin Email</label>
          <input id="admin-email" data-testid="admin-email-input" required type="email" className={inputCls} placeholder="admin@mastersproboxing.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className={labelCls} htmlFor="admin-password">Password</label>
          <input id="admin-password" data-testid="admin-password-input" required type="password" className={inputCls} placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full font-cond font-semibold tracking-[0.25em] text-lg bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white py-4 transition-colors duration-300">
          {loading ? "VERIFYING..." : "ENTER ADMIN"}
        </button>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const { user, setUser, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [site, setSite] = useState({ contact_email: "", contact_phone: "", contact_address: "" });
  const [savingSite, setSavingSite] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "fighter" });
  const [addingUser, setAddingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const isAdmin = user && user.role === "admin";

  const load = useCallback(async () => {
    try {
      const [s, r, u, sc, tk] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/registrations"),
        api.get("/admin/users"),
        api.get("/admin/site-config"),
        api.get("/admin/tickets"),
      ]);
      setStats(s.data);
      setApps(r.data);
      setUsers(u.data);
      setSite(sc.data);
      setTickets(tk.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/registrations/${id}/status`, { status });
      toast.success(`Application ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    try {
      await api.post("/admin/users", newUser);
      toast.success(`${newUser.role === "admin" ? "Admin" : "Fighter"} account created for ${newUser.email}`);
      setNewUser({ name: "", email: "", password: "", role: "fighter" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingUser(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? Their applications and tickets will also be removed.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSite = async (e) => {
    e.preventDefault();
    setSavingSite(true);
    try {
      await api.put("/admin/site-config", site);
      toast.success("Contact details updated — live on the site now");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSite(false);
    }
  };

  if (user === null) {
    return <div className="bg-[#0A0A0A] min-h-screen pt-40 text-center font-mono2 text-xs tracking-[0.3em] text-zinc-500 uppercase">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="bg-[#0A0A0A] min-h-screen" data-testid="admin-page">
        <AdminLogin onDone={(u) => setUser(u)} />
        <p className="text-center pb-16">
          <Link to="/" data-testid="admin-back-home" className="font-mono2 text-[10px] tracking-[0.3em] text-zinc-500 hover:text-[#E10600] uppercase transition-colors">
            ← Back to site
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-zinc-800/80 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <span className="font-display text-xl tracking-[0.15em] text-white">
            MPB <span className="text-[#E10600]">ADMIN</span>
          </span>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="font-mono2 text-[10px] tracking-[0.25em] text-zinc-500 hover:text-white uppercase transition-colors">View Site</Link>
            <button
              onClick={async () => { await logout(); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 font-cond font-semibold tracking-[0.2em] text-xs border border-zinc-700 hover:border-[#E10600] hover:text-[#E10600] text-zinc-300 px-4 py-2 transition-colors duration-300"
            >
              <LogOut className="h-3.5 w-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 space-y-12">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800/60 border border-zinc-800/60" data-testid="admin-stats">
            {[
              { icon: Users, label: "Registrations", val: stats.total, testid: "stat-total" },
              { icon: Clock, label: "Pending", val: stats.pending, testid: "stat-pending" },
              { icon: BadgeCheck, label: "Approved", val: stats.approved, testid: "stat-approved" },
              { icon: Swords, label: "Matches", val: stats.matches, testid: "stat-matches" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0d0d0d] p-6" data-testid={s.testid}>
                <s.icon className="h-5 w-5 text-[#E10600] mb-4" />
                <p className="font-display text-4xl text-white">{s.val}</p>
                <p className="font-mono2 text-[9px] tracking-[0.3em] text-zinc-500 uppercase mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <section data-testid="admin-registrations-section">
          <h2 className="font-display text-3xl uppercase text-white mb-6 flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-[#E10600]" /> Fighter Applications
          </h2>
          {apps.length === 0 ? (
            <p className="font-mono2 text-xs tracking-[0.3em] text-zinc-500 uppercase border border-zinc-800 p-8 text-center" data-testid="admin-empty-state">
              No applications yet
            </p>
          ) : (
            <div className="space-y-3">
              {apps.map((a) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-zinc-800 bg-[#111111] p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5"
                  data-testid={`admin-app-row-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-cond font-bold text-xl uppercase text-white truncate" data-testid={`admin-app-name-${a.id}`}>{a.full_name}</p>
                    <p className="font-mono2 text-[10px] tracking-[0.15em] text-zinc-500 mt-1 break-all">
                      {a.email} · {a.phone} · AGE {a.age} · {a.weight_division?.toUpperCase()} · {a.experience_level?.toUpperCase()} · {a.pro_record}
                    </p>
                    <p className="font-mono2 text-[10px] tracking-[0.15em] text-zinc-600 mt-1">
                      REGISTERED {new Date(a.created_at).toLocaleDateString()}
                    </p>
                    <RecordEditor app={a} reload={load} />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`font-mono2 text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${STATUS_CLS[a.status] || "text-zinc-400 border-zinc-700"}`} data-testid={`admin-app-status-${a.id}`}>
                      {a.status.replace("_", " ")}
                    </span>
                    <button
                      onClick={() => setStatus(a.id, "approved")}
                      disabled={a.status === "approved"}
                      data-testid={`admin-approve-${a.id}`}
                      className="font-cond font-semibold tracking-[0.2em] text-xs bg-green-700 hover:bg-green-600 disabled:opacity-30 text-white px-5 py-2.5 transition-colors duration-300"
                    >
                      APPROVE
                    </button>
                    <button
                      onClick={() => setStatus(a.id, "rejected")}
                      disabled={a.status === "rejected"}
                      data-testid={`admin-reject-${a.id}`}
                      className="font-cond font-semibold tracking-[0.2em] text-xs bg-[#E10600] hover:bg-[#C00500] disabled:opacity-30 text-white px-5 py-2.5 transition-colors duration-300"
                    >
                      REJECT
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        <TicketsAdmin tickets={tickets} reload={load} />

        <section data-testid="admin-users-section">
          <h2 className="font-display text-3xl uppercase text-white mb-6 flex items-center gap-3">
            <Users className="h-6 w-6 text-[#E10600]" /> Website Logins
          </h2>
          <form onSubmit={addUser} className="border border-zinc-800 bg-[#111111] p-6 sm:p-8 mb-6" data-testid="add-user-form">
            <p className="font-mono2 text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-5">Add Account — fighter or another admin</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Full name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required data-testid="add-user-name" />
              <input className={inputCls} type="email" placeholder="Email address" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required data-testid="add-user-email" />
              <input className={inputCls} type="text" placeholder="Password (min. 6 characters)" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required minLength={6} data-testid="add-user-password" />
              <div className="flex gap-3">
                {["fighter", "admin"].map((r) => (
                  <button
                    type="button"
                    key={r}
                    data-testid={`add-user-role-${r}`}
                    onClick={() => setNewUser({ ...newUser, role: r })}
                    className={`flex-1 border py-3 font-cond font-semibold uppercase tracking-[0.2em] text-sm transition-colors duration-300 ${newUser.role === r ? "border-[#E10600] bg-[#E10600]/10 text-white" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={addingUser} data-testid="add-user-submit" className="mt-5 font-cond font-semibold tracking-[0.25em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-8 py-3 transition-colors duration-300">
              {addingUser ? "CREATING..." : "CREATE ACCOUNT"}
            </button>
          </form>
          {users.length === 0 ? (
            <p className="font-mono2 text-xs tracking-[0.3em] text-zinc-500 uppercase border border-zinc-800 p-8 text-center" data-testid="admin-users-empty">
              No accounts yet
            </p>
          ) : (
            <div className="border border-zinc-800 bg-[#111111] divide-y divide-zinc-800/80" data-testid="admin-users-list">
              {users.map((u) => (
                <div key={u.id} className="p-4 sm:px-6" data-testid={`admin-user-row-${u.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                    <span className={`font-mono2 text-[9px] tracking-[0.25em] uppercase border px-2 py-1 w-max ${u.role === "admin" ? "text-[#E10600] border-[#E10600]/50" : "text-zinc-400 border-zinc-700"}`} data-testid={`admin-user-role-${u.id}`}>
                      {u.role}
                    </span>
                    <span className="font-cond font-semibold text-lg text-white" data-testid={`admin-user-name-${u.id}`}>{u.name}</span>
                    <span className="text-sm text-zinc-400 break-all" data-testid={`admin-user-email-${u.id}`}>{u.email}</span>
                    <span className="sm:ml-auto font-mono2 text-[10px] tracking-[0.15em] text-zinc-600">
                      JOINED {new Date(u.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingUserId(editingUserId === u.id ? null : u.id)}
                        data-testid={`admin-user-edit-${u.id}`}
                        className="font-cond font-semibold tracking-[0.2em] text-xs border border-zinc-700 hover:border-[#D4AF37] hover:text-[#D4AF37] text-zinc-300 px-4 py-2 transition-colors duration-300"
                      >
                        EDIT
                      </button>
                      {u.id !== user?.id && (
                        <button
                          onClick={() => deleteUser(u)}
                          data-testid={`admin-user-delete-${u.id}`}
                          className="font-cond font-semibold tracking-[0.2em] text-xs border border-red-900/60 hover:bg-[#E10600] hover:border-[#E10600] text-red-400 hover:text-white px-4 py-2 transition-colors duration-300 w-max"
                        >
                          DELETE
                        </button>
                      )}
                    </div>
                  </div>
                  {editingUserId === u.id && (
                    <UserEditor u={u} apps={apps} reload={load} onDone={() => setEditingUserId(null)} />
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-600 mt-3">Passwords are encrypted and can never be displayed — fighters can reset by re-registering with a new email.</p>
        </section>

        <section data-testid="admin-site-config-section">
          <h2 className="font-display text-3xl uppercase text-white mb-2">Site Contact Details</h2>
          <p className="text-sm text-zinc-500 mb-6">These appear in the website footer — changes go live instantly.</p>
          <form onSubmit={saveSite} className="border border-zinc-800 bg-[#111111] p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="site-config-form">
            <div>
              <label className={labelCls} htmlFor="site-email">Contact Email</label>
              <input id="site-email" data-testid="site-email-input" required className={inputCls} value={site.contact_email} onChange={(e) => setSite({ ...site, contact_email: e.target.value })} />
            </div>
            <div>
              <label className={labelCls} htmlFor="site-phone">Phone / Hotline</label>
              <input id="site-phone" data-testid="site-phone-input" required className={inputCls} value={site.contact_phone} onChange={(e) => setSite({ ...site, contact_phone: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls} htmlFor="site-address">Address</label>
              <input id="site-address" data-testid="site-address-input" required className={inputCls} value={site.contact_address} onChange={(e) => setSite({ ...site, contact_address: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={savingSite} data-testid="site-config-save-button" className="font-cond font-semibold tracking-[0.25em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-10 py-3.5 transition-colors duration-300">
                {savingSite ? "SAVING..." : "SAVE CONTACT DETAILS"}
              </button>
            </div>
          </form>
        </section>

        <MatchesAdmin apps={apps} />
      </div>
    </div>
  );
}
